/**
 * Der Zustand einer **Partie** — alles, was der `BoardState` bewusst nicht weiß.
 *
 * `BoardState` beantwortet „welche Figur steht wo". Der `GameState` hüllt ihn ein und
 * beantwortet „wer spielt mit, in welcher Reihenfolge, wer ist dran, wer hat wie viele
 * Punkte, in welcher Phase ist die Partie". Die Trennung ist dieselbe wie dort: **diese
 * Funktionen prüfen Konsistenz, nicht Spielregeln.** Ob ein Zug erlaubt ist, entscheidet die
 * Regelschicht; ob ein Spieler punktet, entscheidet die Punkteregel. Hier wird nur
 * sichergestellt, dass der Zustand überhaupt widerspruchsfrei ist.
 *
 * **Unveränderlich**, wie der ganze Rest der Engine. Jede Änderung gibt einen neuen Zustand
 * zurück — Voraussetzung für `simulateAction` (§90).
 *
 * **Der RuleConfig-Snapshot gehört zur Partie** (§53). Er liegt hier und nicht irgendwo
 * global: wird morgen `jumper.movement.max` geändert, läuft diese Partie weiter mit ihrem
 * alten Wert. Regelcode liest ihn über den `RuleContext`, der aus diesem Zustand entsteht.
 *
 * **Die Uhr ist Teil des Zustands** (Zyklus 7b). Sie existiert nur in Variante B: die
 * Siegbedingung **bestimmt** die Uhr, Variante A hat keine Bedenkzeit
 * (`docs/OPEN_QUESTIONS.md` Nr. 22) — eine Partie ohne Uhr ist der Normalfall. Dass sie im
 * `GameState` liegt und nicht daneben, hat einen Grund: so wird sie mit dem Snapshot
 * persistiert, den ein Commit ohnehin atomar schreibt (`ARCHITECTURE.md` E19). Eine zweite
 * Schreiboperation nur für die Zeit könnte vom Zustand abweichen.
 *
 * **Was hier noch nicht liegt:**
 *
 * - `pendingAction` — das Preview-/Commit-System ist Phase 5 (§33).
 * - Rehydrierung aus der Datenbank — `createGameState` legt eine **neue** Partie an. Das
 *   Laden eines laufenden Zustands ist Phase 6 und braucht einen eigenen Weg, weil dort
 *   Punkte, Ausgeschiedene und der aktive Spieler mitkommen.
 */
import { emptyBoardState } from './board-state.js';
import { createTimerState } from './timer.js';
/**
 * Wer einen Platz steuert — **nur die Art, nicht das Wie** (KI-Nahtstelle 1,
 * `docs/ARCHITECTURE.md` E10, §112, §137).
 *
 * In V1 ist jeder Spieler `HUMAN`. Das Feld existiert trotzdem von Anfang an, weil es
 * später in jedem Aufrufpfad nachzurüsten wäre.
 *
 * **Seit Zyklus `V2-2a` heißt die zweite Art `BOT` und nicht mehr `LLM`**
 * (`v2Prompt.md` §31, `docs/BOT_ARCHITECTURE.md` 8.1): ein LLM-Spieler ist **eine Strategie
 * unter vielen** — neben Random, Greedy, Alpha-Beta, MaxN und MCTS —, und als Geschwister von
 * `HUMAN` war er ein Kategorienfehler. Daten gingen dabei nicht verloren: `'LLM'` ist nie
 * geschrieben worden, beide Erzeugungsstellen setzten immer `'HUMAN'`.
 *
 * **Der Satz ist seit `V2-2d` ein Wert und nicht nur ein Typ** (`PLAYER_TYPES`), und das hat
 * einen Grund außerhalb der Engine: die Datenbank setzt ihn als `CHECK`-Bedingung durch
 * (`game_players_known_player_type`), und eine Bedingung braucht eine Liste zur Laufzeit. Wäre
 * sie dort von Hand geschrieben, gäbe es zwei Quellen — dasselbe Argument wie bei `SECTOR_IDS`
 * und `PERMANENT_EVENT_TYPES`, die das Schema aus demselben Grund importiert.
 *
 * **Welche Strategie ein Bot verfolgt, steht hier bewusst nicht** (`BOT_ARCHITECTURE.md` 8.2).
 * Eine `BotDefinition` ist keine Spiellogik; läge sie im Zustand, hätte diese pure Engine einen
 * Bot-Begriff und jede Testfixture müsste ihn mitführen. Sie liegt deshalb in einer Spalte an
 * `game_players` — dasselbe Argument wie bei `show_coordinates` (**E30**).
 */
export const PLAYER_TYPES = ['HUMAN', 'BOT'];
/**
 * Welche Statusübergänge §47 zulässt.
 *
 * ```
 * CREATED → LOBBY → SETUP → PLAYING → FINISHED
 *                              ↕
 *                            PAUSED
 * ```
 *
 * `ABORTED` ist nach §47 „optional" und ohne Pfeile gezeichnet. **Abgeleitet:** es ist aus
 * jedem laufenden Status erreichbar und wie `FINISHED` endgültig — ein Abbruch kann jede
 * Phase treffen, eine abgebrochene Partie läuft nicht weiter.
 *
 * **Abgeleitet, bewusst streng:** aus `PAUSED` führt kein Weg nach `FINISHED`. §47 zeichnet
 * den Abschluss nur hinter `PLAYING`. Eine pausierte Partie, die enden soll, wird
 * fortgesetzt oder abgebrochen. Sollte sich das als zu streng erweisen, sagt es ein Test —
 * das ist besser, als den Graphen vorsorglich aufzuweichen.
 */
const ALLOWED_TRANSITIONS = {
    CREATED: ['LOBBY', 'ABORTED'],
    LOBBY: ['SETUP', 'ABORTED'],
    SETUP: ['PLAYING', 'ABORTED'],
    PLAYING: ['PAUSED', 'FINISHED', 'ABORTED'],
    PAUSED: ['PLAYING', 'ABORTED'],
    FINISHED: [],
    ABORTED: [],
};
/**
 * Prüft, dass Uhr und Siegbedingung zusammenpassen (Nr. 20 und Nr. 22).
 *
 * Beide Richtungen sind Fehler, und zwar verschiedene:
 *
 * - **Variante B ohne Uhr** wäre eine Partie, die nicht enden kann — das Punktziel ist dort
 *   inaktiv, und ohne Bedenkzeit fehlt jedes Abbruchkriterium.
 * - **Variante A mit Uhr** wäre ein widersprüchlicher Auftrag: sie könnte nie ablaufen
 *   (Nr. 22) und würde den Host glauben lassen, er hätte eine Zeitpartie konfiguriert.
 */
function requireTimerMatchesVictory(victory, timer, players) {
    if (victory === 'TIME' && timer === undefined) {
        throw new Error('Variante B (Zeitsieg) braucht eine Bedenkzeit — ohne sie könnte die Partie nicht ' +
            'enden (docs/OPEN_QUESTIONS.md Nr. 20)');
    }
    if (victory === 'POINTS' && timer !== undefined) {
        throw new Error('Variante A (Punktsieg) hat keine Bedenkzeit — die Siegbedingung bestimmt die Uhr ' +
            '(docs/OPEN_QUESTIONS.md Nr. 22)');
    }
    if (timer === undefined) {
        return;
    }
    const known = new Set(players.map((player) => player.id));
    for (const id of timer.remaining.keys()) {
        if (!known.has(id)) {
            throw new Error(`Die Bedenkzeit führt '${id}', der nicht in dieser Partie spielt`);
        }
    }
    for (const player of players) {
        if (!timer.remaining.has(player.id)) {
            throw new Error(`Für '${player.id}' fehlt eine Bedenkzeit`);
        }
    }
}
function requireNoDuplicates(values, label) {
    const seen = new Set();
    for (const value of values) {
        if (seen.has(value)) {
            throw new Error(`${label}: '${value}' kommt mehrfach vor`);
        }
        seen.add(value);
    }
}
/**
 * Wie viele Doppelzüge eine Teamzuweisung **unvermeidbar** erzwingt.
 *
 * Die Zugreihenfolge ist ein Kreis, weil sie sich wiederholt. Ein Team mit `k` Mitgliedern
 * lässt sich auf `n` Plätzen nur trennen, wenn `k ≤ n/2` — jedes Mitglied braucht einen
 * Fremden daneben. Darüber hinaus bleiben `2·k − n` Nachbarschaften übrig:
 *
 * ```
 * 1:1 · 2:2 · 2:1:1 · 3:3 · 2:2:2   →  0
 * 2:1  (3 Plätze, k = 2)            →  1
 * 3:2  (5 Plätze, k = 3)            →  1
 * 4:2  (6 Plätze, k = 4)            →  2
 * ```
 *
 * Bei drei Plätzen grenzt jeder an beide anderen, dort ist also nichts zu retten. Mehr als
 * ein Team kann die Hälfte nie überschreiten, deshalb genügt das größte.
 *
 * Phase 8 kann den Host damit warnen, bevor die Partie startet.
 */
export function minimalTeamAdjacencies(players) {
    const sizes = new Map();
    for (const player of players) {
        if (player.team === undefined)
            continue;
        sizes.set(player.team, (sizes.get(player.team) ?? 0) + 1);
    }
    const largest = Math.max(0, ...sizes.values());
    return Math.max(0, 2 * largest - players.length);
}
/** Die Paare aufeinanderfolgender Spieler aus demselben Team — zyklisch. */
function teamAdjacencies(turnOrder, teamById) {
    if (turnOrder.length < 2)
        return [];
    const found = [];
    for (let index = 0; index < turnOrder.length; index++) {
        const current = turnOrder[index];
        const next = turnOrder[(index + 1) % turnOrder.length];
        const team = teamById.get(current);
        if (team !== undefined && teamById.get(next) === team) {
            found.push(`'${current}'→'${next}' (Team '${team}')`);
        }
    }
    return found;
}
/**
 * Prüft §9: Mitglieder desselben Teams ziehen möglichst nicht direkt hintereinander.
 *
 * **Zyklisch geprüft**, weil die Reihenfolge sich wiederholt: nach dem letzten Spieler kommt
 * wieder der erste. Wären das zwei Teammitglieder, zöge das Team über jeden Rundenwechsel
 * hinweg doppelt. Beide in §9 genannten Muster (3:3 und 2:2:2) erfüllen die Prüfung ohne
 * eine einzige Nachbarschaft.
 *
 * **§9 ist ein Minimierungsgebot, kein absolutes Verbot** (`docs/OPEN_QUESTIONS.md` Nr. 24,
 * vom User am 2026-10-02 entschieden): ungleiche Teams sind ausdrücklich erlaubt — zwei
 * Anfänger gegen einen erfahrenen Spieler funktioniert am physischen Brett und soll hier
 * ebenso gehen. Wo eine Nachbarschaft geometrisch unvermeidbar ist, nimmt die unterlegene
 * Seite sie hin. Geprüft wird deshalb gegen `minimalTeamAdjacencies`: mehr als nötig ist ein
 * Fehler, genau so viele wie nötig sind erlaubt.
 *
 * Die Prüfung gilt für die **Erzeugung** der Reihenfolge. Scheidet später ein Spieler aus,
 * bleibt die Reihenfolge stehen und er wird übersprungen — dann können weitere
 * Nachbarschaften entstehen (`docs/OPEN_QUESTIONS.md` Nr. 23).
 */
function requireMinimalTeamAdjacency(players, turnOrder, teamById) {
    const allowed = minimalTeamAdjacencies(players);
    const found = teamAdjacencies(turnOrder, teamById);
    if (found.length > allowed) {
        throw new Error(`Zugreihenfolge verletzt §9: ${found.length} Teammitglieder-Paare ziehen ` +
            `hintereinander, bei dieser Aufteilung sind höchstens ${allowed} unvermeidbar — ` +
            found.join(', '));
    }
}
/**
 * Vor dem Start: hier darf eine Partie noch ohne Spieler sein (Nr. 31).
 *
 * In der Lobby ist die Farbwahl optional (Nr. 29), und ein entfallender Farbplatz macht seinen
 * Spieler zum Wartenden (Nr. 30) — auch den Host. Wer direkt nach dem Anlegen die Farben
 * umstellt, hat damit vorübergehend eine Lobby ohne eine einzige Zuordnung. Das ist ein
 * gültiger Zustand und keine Partie.
 *
 * Ab `SETUP` gilt die Invariante wieder: dort wird aufgestellt, und ohne Spieler gäbe es nichts
 * aufzustellen. `start` verlangt ohnehin jeden Platz belegt (Nr. 28).
 */
const BEFORE_START = ['CREATED', 'LOBBY'];
/**
 * Liegt dieser Status **vor** dem Start der Partie?
 *
 * Exportiert, weil der Server dieselbe Frage stellt: in der Lobby lebt die Wahrheit relational
 * (`ARCHITECTURE.md` E22), mit dem Start friert der Zustand ein. Die zwei Statusnamen an einer
 * zweiten Stelle aufzuzählen wäre eine zweite Liste, die bei einer Änderung von §47 zurückbleibt.
 */
export function isBeforeStart(status) {
    return BEFORE_START.includes(status);
}
/**
 * Legt eine neue Partie an.
 *
 * Geprüft wird, was einen Zustand in sich widersprüchlich machen würde: mindestens ein
 * Spieler (ab `SETUP`, siehe `BEFORE_START`), eindeutige IDs, **eindeutige Sektoren** (sonst
 * stellten zwei Spieler in derselben Startzone auf), und eine Zugreihenfolge, die genau die
 * angetretenen Spieler enthält. Dazu §9 für Teammodi.
 */
export function createGameState(input) {
    const status = input.status ?? 'CREATED';
    if (input.players.length === 0 && !isBeforeStart(status)) {
        throw new Error(`Eine Partie braucht ab ${status} mindestens einen Spieler`);
    }
    requireNoDuplicates(input.players.map((player) => player.id), 'Spieler-ID');
    requireNoDuplicates(input.players.map((player) => player.sector), 'Sektor');
    requireNoDuplicates(input.turnOrder, 'Zugreihenfolge');
    if (input.turnOrder.length !== input.players.length) {
        throw new Error(`Die Zugreihenfolge enthält ${input.turnOrder.length} Spieler, angetreten sind ` +
            `${input.players.length}`);
    }
    const teamById = new Map(input.players.map((player) => [player.id, player.team]));
    for (const id of input.turnOrder) {
        if (!teamById.has(id)) {
            throw new Error(`Die Zugreihenfolge nennt '${id}', der nicht in dieser Partie spielt`);
        }
    }
    requireMinimalTeamAdjacency(input.players, input.turnOrder, teamById);
    // Die Uhr entsteht mit der Partie, steht aber noch: sie startet mit dem ersten Zug (§38).
    const timer = input.timeBudgetMs === undefined
        ? undefined
        : createTimerState(input.players.map((player) => player.id), input.timeBudgetMs);
    requireTimerMatchesVictory(input.victory, timer, input.players);
    return {
        status,
        players: input.players,
        turnOrder: input.turnOrder,
        activePlayer: undefined,
        turnNumber: 0,
        eliminated: [],
        setupConfirmed: [],
        scores: new Map(input.players.map((player) => [player.id, 0])),
        victory: input.victory,
        boardState: input.boardState ?? emptyBoardState(),
        pendingAction: undefined,
        outcome: undefined,
        timer,
        ruleConfig: input.ruleConfig,
    };
}
/**
 * Stellt eine laufende Partie aus gespeicherten Daten wieder her (Phase 6).
 *
 * `createGameState` legt eine **neue** Partie an und setzt Punkte, Zugnummer und Ausgeschiedene
 * auf den Anfangswert. Hier ist es umgekehrt: alles kommt von außen herein, und geprüft wird,
 * dass es zusammenpasst. Dieselben Grundprüfungen wie beim Anlegen gelten weiter — eindeutige
 * Spieler, eindeutige Sektoren, eine vollständige Zugreihenfolge, §9 — dazu die Prüfungen, die
 * nur ein geladener Zustand verletzen kann.
 *
 * **Warum so streng:** diese Funktion ist die Grenze zwischen Datenbank und Spiellogik. Was
 * hier durchkommt, gilt in der ganzen Engine als widerspruchsfrei. Ein stillschweigend
 * reparierter Zustand wäre später nicht mehr von einem echten zu unterscheiden.
 *
 * **Die Punkte werden kanonisiert:** fehlende Einträge ergänzt die Funktion mit null, fremde
 * weist sie ab. Danach hat jeder Spieler genau einen Eintrag.
 */
export function restoreGameState(input) {
    // Ohne `timeBudgetMs`: die Uhr wird **nicht** neu angelegt, sonst bekäme jeder
    // Serverneustart dem Spieler sein Budget zurück. Deshalb hier auch `victory: 'POINTS'` —
    // die eigentliche Prüfung passiert unten mit der gespeicherten Uhr.
    const base = createGameState({
        players: input.players,
        turnOrder: input.turnOrder,
        victory: 'POINTS',
        ruleConfig: input.ruleConfig,
        boardState: input.boardState,
        status: input.status,
    });
    requireTimerMatchesVictory(input.victory, input.timer, input.players);
    const known = new Set(input.players.map((player) => player.id));
    if (input.activePlayer !== undefined && !input.turnOrder.includes(input.activePlayer)) {
        throw new Error(`Der aktive Spieler '${input.activePlayer}' steht nicht in der Zugreihenfolge`);
    }
    requireNoDuplicates(input.eliminated, 'Ausgeschiedener');
    for (const id of input.eliminated) {
        if (!known.has(id)) {
            throw new Error(`'${id}' ist als ausgeschieden vermerkt, spielt aber nicht mit`);
        }
    }
    // Dieselben zwei Prüfungen wie bei den Ausgeschiedenen, aus demselben Grund: ein
    // Eintrag für einen Fremden oder ein doppelter wäre ein Fehler beim Speichern, und er
    // würde später als rätselhaftes Verhalten in der Aufstellung auffallen.
    requireNoDuplicates(input.setupConfirmed, 'Aufstellungsbestätigung');
    for (const id of input.setupConfirmed) {
        if (!known.has(id)) {
            throw new Error(`'${id}' hat eine Aufstellung bestätigt, spielt aber nicht mit`);
        }
    }
    if (!Number.isInteger(input.turnNumber) || input.turnNumber < 0) {
        throw new Error(`Die Zugnummer ${input.turnNumber} ist keine gültige Zählung`);
    }
    const scores = new Map(input.players.map((player) => [player.id, 0]));
    for (const [id, points] of input.scores) {
        if (!known.has(id)) {
            throw new Error(`Punkteintrag für '${id}', der nicht mitspielt`);
        }
        scores.set(id, points);
    }
    // §50: die Preview gehört dem Spieler am Zug. Alles andere wäre ein Fehler beim Speichern.
    if (input.pendingAction !== undefined && input.pendingAction.player !== input.activePlayer) {
        throw new Error(`Die gespeicherte Preview gehört '${input.pendingAction.player}', am Zug ist aber ` +
            `'${input.activePlayer ?? 'niemand'}' (§50)`);
    }
    // Die Gegenrichtung gilt nicht: eine Partie darf beendet sein, ohne dass eine Seite
    // gewonnen hat — der Host kann sie abschließen (§47).
    if (input.outcome !== undefined && input.status !== 'FINISHED') {
        throw new Error(`Ein Ergebnis gibt es nur im Status FINISHED, nicht in ${input.status}`);
    }
    // Verdeckte Figuren gibt es nur in einer Projektion der Aufstellung, und nur von Mitspielern.
    if (input.coveredPieces !== undefined) {
        if (input.status !== 'SETUP') {
            throw new Error(`Verdeckte Figuren gibt es nur in SETUP, nicht in ${input.status}`);
        }
        for (const piece of input.coveredPieces) {
            if (!known.has(piece.owner)) {
                throw new Error(`Eine verdeckte Figur gehört '${piece.owner}', der nicht mitspielt`);
            }
        }
    }
    return {
        ...base,
        victory: input.victory,
        activePlayer: input.activePlayer,
        turnNumber: input.turnNumber,
        eliminated: input.eliminated,
        scores,
        pendingAction: input.pendingAction,
        outcome: input.outcome,
        timer: input.timer,
        setupConfirmed: input.setupConfirmed,
        ...(input.setupDeadlineAt === undefined ? {} : { setupDeadlineAt: input.setupDeadlineAt }),
        ...(input.coveredPieces === undefined ? {} : { coveredPieces: input.coveredPieces }),
    };
}
export function playerOf(state, id) {
    return state.players.find((player) => player.id === id);
}
/** Wie `playerOf`, wirft aber — für Aufrufer, denen ein unbekannter Spieler ein Fehler ist. */
export function requirePlayer(state, id) {
    const player = playerOf(state, id);
    if (player === undefined) {
        throw new Error(`Kein Spieler mit der ID '${id}' in dieser Partie`);
    }
    return player;
}
export function teamOf(state, id) {
    return requirePlayer(state, id).team;
}
/** Die Mitspieler desselben Teams — ohne den Spieler selbst. Leer, wenn er kein Team hat. */
export function teammates(state, id) {
    const team = teamOf(state, id);
    if (team === undefined)
        return [];
    return state.players.filter((player) => player.team === team && player.id !== id);
}
/** Die Seite, zu der ein Spieler gehört — intern, ohne Spielerprüfung. */
function sideOfPlayer(player) {
    return player.team ?? player.id;
}
/**
 * Die **Seite**, über die gewonnen wird: das Team, oder der Spieler selbst (§26, §42).
 *
 * Die Siegbedingung rechnet durchgehend in Seiten und nie in Spielern — nur so gilt
 * derselbe Code für 1:1, FFA und Teammodi. Team-IDs und Spieler-IDs müssen sich deshalb
 * unterscheiden; das stellt die Partieerstellung sicher (Phase 8).
 */
export function sideOf(state, id) {
    return sideOfPlayer(requirePlayer(state, id));
}
/** Die Punkte dieses einen Spielers. */
export function scoreOf(state, id) {
    requirePlayer(state, id);
    return state.scores.get(id) ?? 0;
}
/** Die Punkte seiner **Seite**: in Teamspielen die Summe des Teams (§23). */
export function sideScore(state, id) {
    const side = sideOf(state, id);
    return state.players
        .filter((player) => sideOfPlayer(player) === side)
        .reduce((sum, player) => sum + (state.scores.get(player.id) ?? 0), 0);
}
/**
 * Schreibt diesem Spieler Punkte zu.
 *
 * In Teamspielen zählt der Punkt dadurch automatisch für das Team, weil `sideScore`
 * summiert — und der individuelle Beitrag bleibt trotzdem erkennbar (§23).
 */
export function addScore(state, id, points) {
    requirePlayer(state, id);
    const scores = new Map(state.scores);
    scores.set(id, (scores.get(id) ?? 0) + points);
    return { ...state, scores };
}
/**
 * **Setzt** den Punktestand dieses Spielers — der Debug-Eingriff aus §60, Zyklus 11f.
 *
 * Der Unterschied zu `addScore` ist der Zweck: dort wird ein erspielter Punkt **zugerechnet**,
 * hier wird ein Stand **behauptet**. Deshalb zwei Funktionen und kein Vorzeichenspiel — eine
 * Partie auf 2:1 zu stellen, ohne zu wissen, wo sie steht, wäre mit `addScore` eine Rechnung
 * des Aufrufers, und die wäre im Nachhinein nicht mehr als Eingriff erkennbar.
 *
 * **Die Siegprüfung passiert hier nicht** (Nr. 39). Sie hängt nach §26 am Zugbeginn, und ein
 * Eingriff ist kein Zug (Nr. 37): eine Partie darf nach diesem Aufruf das Punktziel erfüllen,
 * ohne beendet zu sein. Die nächste Aktion beendet sie dann.
 *
 * **Ein Punktestand ist eine Zählung.** Ein negativer oder gebrochener Wert ist deshalb kein
 * Regelverstoß, über den sich streiten ließe, sondern eine Konsistenzverletzung — und die wird
 * hier geworfen, nicht abgewogen (Nr. 38, Klasse C; dieselbe Arbeitsteilung wie in
 * `board-state.ts`). Der Debug-Eingriff prüft vorher und antwortet mit einem Code, statt diese
 * Ausnahme bis zum Aufrufer durchzulassen.
 */
export function setScore(state, id, points) {
    requirePlayer(state, id);
    if (!Number.isInteger(points) || points < 0) {
        throw new Error(`'${points}' ist kein Punktestand — ein Punktestand ist eine nicht negative Zählung`);
    }
    const scores = new Map(state.scores);
    scores.set(id, points);
    return { ...state, scores };
}
/**
 * Setzt die Punkte der ganzen **Seite** auf null — `resetScoreOnFlagCapture` (§24).
 *
 * Bei Teamspielen trifft der Reset den gesamten TeamScore, also jedes Mitglied.
 */
export function resetSideScore(state, id) {
    const side = sideOf(state, id);
    const scores = new Map(state.scores);
    for (const player of state.players) {
        if (sideOfPlayer(player) === side)
            scores.set(player.id, 0);
    }
    return { ...state, scores };
}
/** Wechselt die Partiephase — nur entlang der Übergänge aus §47. */
export function setStatus(state, status) {
    if (!ALLOWED_TRANSITIONS[state.status].includes(status)) {
        throw new Error(`Statusübergang ${state.status} → ${status} ist nicht erlaubt (§47)`);
    }
    return { ...state, status };
}
export function isEliminated(state, id) {
    return state.eliminated.includes(id);
}
/**
 * Trägt einen Spieler als ausgeschieden ein.
 *
 * **Das ist reine Buchführung.** Die Regel zum Zeitablauf verlangt mehr — die Figuren des
 * Spielers kommen vom Brett und eine offene Preview verfällt (`docs/GAME_RULES.md`,
 * Abschnitt 9, `OPEN_QUESTIONS.md` Nr. 3). Das steht in der gekapselten Timeout-Regel, nicht
 * hier: dieselbe Trennung wie beim `BoardState`, der Konsistenz prüft und keine Spielregeln
 * kennt. Wer diese Funktion direkt aufruft, lässt die Figuren stehen.
 */
export function eliminatePlayer(state, id) {
    requirePlayer(state, id);
    if (isEliminated(state, id)) {
        throw new Error(`Spieler '${id}' ist bereits ausgeschieden`);
    }
    return { ...state, eliminated: [...state.eliminated, id] };
}
/**
 * Die Zugreihenfolge ohne die Ausgeschiedenen — in unveränderter Reihenfolge.
 *
 * Die Reihenfolge selbst bleibt dabei stehen und wird **nicht** neu erzeugt
 * (`docs/OPEN_QUESTIONS.md` Nr. 23). Dass dadurch zwei Teammitglieder benachbart werden
 * können, ist vom User so entschieden.
 */
export function activePlayers(state) {
    return state.turnOrder.filter((id) => !isEliminated(state, id));
}
/**
 * Verwirft eine offene Preview (§31).
 *
 * Gebraucht an zwei Stellen: wenn der Spieler abbricht, und wenn seine Bedenkzeit abläuft
 * (§39, `OPEN_QUESTIONS.md` Nr. 3) — dort **verfällt** sie und wird ausdrücklich nicht
 * committet. Ohne offene Preview bleibt der Zustand unverändert.
 */
export function clearPendingAction(state) {
    if (state.pendingAction === undefined)
        return state;
    return { ...state, pendingAction: undefined };
}
/** Tauscht das Brett aus und lässt alles andere unberührt — für die Zugausführung. */
export function setBoardState(state, boardState) {
    return { ...state, boardState };
}
/** Die Spieler-Sektor-Zuordnung, wie der `RuleContext` sie braucht. */
export function sectorsOf(state) {
    return new Map(state.players.map((player) => [player.id, player.sector]));
}
/** Die Teamzuordnung, wie der `RuleContext` sie braucht — nur Spieler mit Team (Nr. 54). */
export function teamsOf(state) {
    const teams = new Map();
    for (const player of state.players) {
        if (player.team !== undefined)
            teams.set(player.id, player.team);
    }
    return teams;
}
//# sourceMappingURL=game-state.js.map